import { translate } from "app/filters/i18nFilter";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";

import Toast from "../components/Toast/Toast";
import type { IExportDomainsService } from "../types/exportDomainsService";

const createExportDomainsService = (integrationName: CrmIntegrationProvider): IExportDomainsService => {
    const name = integrationName === "salesforce" ? "sf" : integrationName === "dynamics" ? "dynamics" : "hs";

    return {
        getToastComponent(text: string) {
            return <Toast text={text} />;
        },
        getSuccessToastTitle(processedRecords: number, domains: number): string {
            if (processedRecords === domains) {
                return translate(`si.management.${name}.settings_export.toast.success_all.content`);
            }

            if (domains === 1) {
                return translate(`si.management.${name}.settings_export.toast.success_not_one.content`, {
                    processedRecords,
                    domains,
                });
            }

            return translate(`si.management.${name}.settings_export.toast.success_not_all.content`, {
                processedRecords,
                domains,
            });
        },
        getExportingToastTitle(domains: number): string {
            if (domains === 1) {
                return translate(`si.management.${name}.settings_export.toast.exporting.content_one_website`, {
                    domains,
                });
            }

            return translate(`si.management.${name}.settings_export.toast.exporting.content`, {
                domains,
            });
        },
        getErrorToastTitle(): string {
            return translate(`si.management.${name}.settings_export.toast.error.content`);
        },
        createToast(children: string) {
            return {
                children,
                timestamp: Date.now(),
            };
        },
    };
};

const createLazyExportDomainsService = (integrationName: CrmIntegrationProvider): IExportDomainsService =>
    new Proxy({} as IExportDomainsService, {
        get(_target, prop, receiver) {
            const service = createExportDomainsService(integrationName);
            const value = Reflect.get(service as object, prop, receiver);

            return typeof value === "function" ? value.bind(service) : value;
        },
    });

export const salesforceDomainService = createLazyExportDomainsService("salesforce");
export const hubspotDomainService = createLazyExportDomainsService("hubspot");
export const dynamicsDomainService = createLazyExportDomainsService("dynamics");
